import {useEffect, useSyncExternalStore} from "react";
import store from "@/store";
import {RosterLineupAPI as api} from "@/api/roster_lineup";
import {
	getDsContext,
	isDsContextReady,
	onDsContextChange,
} from "@/utils/dsContext";
const empty = {
	status: "idle",
	data: null,
	pending: false,
	saving: false,
	error: null,
	conflict: false,
};
let state = {};
let revisions = {};
const requests = new Map();
const jobs = new Map();
const listeners = new Set();
const publish = (id, patch) => {
	state = {...state, [id]: {...(state[id] || empty), ...patch}};
	listeners.forEach((fn) => fn());
};
const normalize = (data) => ({
	...data,
	stacks: Object.fromEntries(
		(data.slots || []).map((s) => [s.slotId, s.playerIds || []]),
	),
});
onDsContextChange(() => {
	jobs.forEach((j) => clearTimeout(j.timer));
	jobs.clear();
	requests.clear();
	state = {};
	revisions = {};
	listeners.forEach((fn) => fn());
});
export const lineupData = (id) => {
	getDsContext();
	return state[id] || empty;
};
export const refreshLineup = (id, discard = false) => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(Error("Sessione non disponibile"));
	if (requests.has(id) && !discard) return requests.get(id);
	if (jobs.get(id)?.pending && !discard) return Promise.resolve();
	if (discard) {
		const job = jobs.get(id);
		if (job?.saving)
			return Promise.reject(Error("Attendi il salvataggio in corso"));
		clearTimeout(job?.timer);
		jobs.delete(id);
		requests.delete(id);
		revisions[id] = (revisions[id] || 0) + 1;
	}
	publish(id, {status: state[id]?.data ? "ready" : "loading", error: null});
	const revision = revisions[id] || 0;
	const request = api
		.get(id)
		.then((result) => {
			if (context !== getDsContext() || revision !== (revisions[id] || 0))
				return;
			if (!jobs.get(id)?.pending)
				publish(id, {
					status: "ready",
					data: normalize(result),
					pending: false,
					saving: false,
					conflict: false,
					error: null,
				});
		})
		.catch((error) => {
			if (context === getDsContext() && revision === (revisions[id] || 0))
				publish(id, {status: "error", data: null, error});
			throw error;
		})
		.finally(() => {
			if (requests.get(id) === request) requests.delete(id);
		});
	requests.set(id, request);
	return request;
};
export const flushLineup = (id, keepalive = false) => {
	const context = getDsContext();
	const job = jobs.get(id);
	if (!job?.pending || job.context !== context || state[id]?.conflict)
		return Promise.resolve();
	clearTimeout(job.timer);
	if (job.saving)
		return job.promise.then(() =>
			job.pending ? flushLineup(id, keepalive) : undefined,
		);
	const sequence = job.sequence;
	const draft = job.draft;
	job.saving = true;
	publish(id, {saving: true, error: null});
	const slots = Object.entries(draft.stacks).map(([slotId, playerIds]) => ({
		slotId,
		code: draft.slotCodes[slotId],
		playerIds,
	}));
	job.promise = api
		.put(
			id,
			{
				moduleId: draft.moduleId,
				slots,
				showNationality: draft.showNationality,
				showAge: draft.showAge,
				version: job.version,
			},
			job.token,
			keepalive,
		)
		.then((result) => {
			if (context !== getDsContext()) return;
			job.version = result.version;
			const saved = normalize(result);
			if (sequence === job.sequence) {
				job.pending = false;
				job.draft = saved;
				publish(id, {data: saved, pending: false});
			} else
				publish(id, {
					data: {...job.draft, version: result.version, exists: true},
				});
		})
		.catch((error) => {
			if (context === getDsContext())
				publish(id, {
					error,
					conflict: [400, 409].includes(error.response?.status),
					...([401, 403].includes(error.response?.status)
						? {status: "error", data: null}
						: {}),
				});
			throw error;
		})
		.finally(() => {
			job.saving = false;
			if (context === getDsContext())
				publish(id, {saving: false, pending: job.pending});
		});
	return job.promise.then(() => {
		if (context === getDsContext() && job.pending && !state[id]?.error)
			return flushLineup(id, keepalive);
	});
};
export const changeLineup = (id, value) => {
	const context = getDsContext();
	const current = lineupData(id);
	if (
		!isDsContextReady() ||
		current.status !== "ready" ||
		!current.data?.canWrite ||
		current.conflict
	)
		return;
	let job = jobs.get(id);
	if (!job) {
		job = {
			context,
			token: store.getState().token,
			sequence: 0,
			version: current.data.version,
		};
		jobs.set(id, job);
	}
	if (!job.pending) job.version = current.data.version;
	job.draft = {...current.data, ...value};
	job.pending = true;
	job.sequence++;
	revisions[id] = (revisions[id] || 0) + 1;
	clearTimeout(job.timer);
	publish(id, {data: job.draft, pending: true, error: null});
	job.timer = setTimeout(() => flushLineup(id).catch(() => {}), 600);
};
if (typeof window !== "undefined") {
	window.addEventListener("beforeunload", (event) => {
		if ([...jobs.values()].some((j) => j.pending)) {
			event.preventDefault();
			event.returnValue = "";
		}
	});
	window.addEventListener("pagehide", () =>
		jobs.forEach((j, id) => {
			if (j.pending) flushLineup(id, true).catch(() => {});
		}),
	);
}
export const useRosterLineup = (id) => {
	const context = getDsContext();
	useSyncExternalStore(
		(fn) => {
			listeners.add(fn);
			return () => listeners.delete(fn);
		},
		() => state,
	);
	useEffect(() => {
		if (!id || !isDsContextReady()) return;
		const reload = () => {
			if (!document.hidden) refreshLineup(id).catch(() => {});
		};
		reload();
		window.addEventListener("focus", reload);
		const timer = setInterval(reload, 30000);
		return () => {
			window.removeEventListener("focus", reload);
			clearInterval(timer);
			if (context === getDsContext()) flushLineup(id).catch(() => {});
		};
	}, [id, context]);
	return {
		...lineupData(id),
		refresh: () => refreshLineup(id, true).catch(() => {}),
		retry: () => flushLineup(id).catch(() => {}),
		change: (value) => changeLineup(id, value),
	};
};
